import { Check, CircleCheck, type LucideIcon } from 'lucide-react'
import { cn } from '../lib/utils'

export type StepState = 'completed' | 'progress' | 'upcoming'

export type GroupStatusStepView = {
  id: number
  label: string
  shortLabel?: string
  icon: LucideIcon
  state: StepState
}

interface GroupStatusIndicatorProps {
  steps: GroupStatusStepView[]
  className?: string
}

function StepIcon({
  step,
  size = 'md',
}: {
  step: GroupStatusStepView
  size?: 'sm' | 'md'
}) {
  const Icon = step.icon
  const compact = size === 'sm'

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full border-2 bg-white',
        compact ? 'h-8 w-8' : 'h-10 w-10',
        step.state === 'completed' && 'border-red-500 bg-red-500',
        step.state === 'progress' && 'border-red-500 bg-white',
        step.state === 'upcoming' && 'border-gray-300'
      )}
    >
      {step.state === 'completed' && (
        <Check className={cn(compact ? 'h-4 w-4' : 'h-5 w-5', 'text-white')} strokeWidth={3} />
      )}
      {step.state === 'progress' && (
        <CircleCheck className={cn(compact ? 'h-4 w-4' : 'h-5 w-5', 'text-red-500')} strokeWidth={2.5} />
      )}
      {step.state === 'upcoming' && (
        <Icon className={cn(compact ? 'h-4 w-4' : 'h-5 w-5', 'text-gray-400')} />
      )}
    </div>
  )
}

function stepTextClass(state: StepState) {
  return state === 'upcoming' ? 'text-gray-400' : 'text-gray-900'
}

export default function GroupStatusIndicator({
  steps,
  className,
}: GroupStatusIndicatorProps) {
  const allCompleted = steps.every((step) => step.state === 'completed')
  const progressIndex = steps.findIndex((step) => step.state === 'progress')
  const lastCompletedIndex = steps.map((step) => step.state).lastIndexOf('completed')
  const lineIndex = allCompleted
    ? steps.length - 1
    : progressIndex >= 0
      ? progressIndex
      : Math.max(0, lastCompletedIndex)
  const progressRatio = allCompleted || steps.length <= 1
    ? 1
    : lineIndex / (steps.length - 1)

  return (
    <div className={cn('w-full min-w-0', className)}>
      <ol className="space-y-0 sm:hidden">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1
          const connectorDone = step.state === 'completed'

          return (
            <li key={step.id} className="flex gap-3">
              <div className="flex w-8 shrink-0 flex-col items-center">
                <StepIcon step={step} size="sm" />
                {!isLast && (
                  <div
                    className={cn(
                      'my-1 w-0.5 min-h-4 flex-1',
                      connectorDone ? 'bg-red-500' : 'bg-gray-200'
                    )}
                  />
                )}
              </div>
              <p
                className={cn(
                  'min-w-0 flex-1 pt-1.5 text-sm font-medium leading-snug',
                  !isLast && 'pb-4',
                  stepTextClass(step.state)
                )}
              >
                {step.label}
              </p>
            </li>
          )
        })}
      </ol>

      <div className="relative hidden items-start justify-between sm:flex">
        <div className="absolute top-5 left-[calc(100%/12)] right-[calc(100%/12)] h-0.5 bg-gray-200">
          <div
            className="h-full bg-red-500 transition-all duration-500"
            style={{ width: `${progressRatio * 100}%` }}
          />
        </div>

        {steps.map((step) => (
          <div key={step.id} className="relative z-10 flex min-w-0 flex-1 flex-col items-center">
            <div className="mb-2">
              <StepIcon step={step} />
            </div>
            <p
              className={cn(
                'w-full px-1 text-center text-xs font-medium leading-tight break-words md:text-sm',
                stepTextClass(step.state)
              )}
            >
              {step.shortLabel ?? step.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
